import { createTheme, type Theme } from '@mui/material/styles'
import { palette, radius, spacingUnit, type ThemeMode } from './tokens.js'

/**
 * Thèmes MUI dérivés des jetons. Les rôles sémantiques (bg.canvas, text.secondary…)
 * sont mappés sur les slots MUI pour que les composants standards les respectent
 * sans surcharge au cas par cas.
 */
function build(mode: ThemeMode): Theme {
  const dark = mode === 'dark'

  return createTheme({
    palette: {
      mode,
      primary: { main: dark ? palette.brand[400] : palette.brand[500] },
      success: { main: palette.success[500] },
      warning: { main: palette.warning[500] },
      error: { main: palette.danger[500] },
      info: { main: palette.info[500] },
      background: {
        default: dark ? palette.neutral[950] : palette.neutral[50],
        paper: dark ? palette.neutral[900] : palette.neutral[0],
      },
      text: {
        primary: dark ? palette.neutral[50] : palette.neutral[900],
        secondary: dark ? palette.neutral[400] : palette.neutral[500],
        disabled: dark ? palette.neutral[600] : palette.neutral[400],
      },
      divider: dark ? palette.neutral[700] : palette.neutral[200],
    },

    spacing: spacingUnit,
    shape: { borderRadius: radius.md },

    typography: {
      // Nunito : police de marque commune à tous les projets, auto-hébergée
      // dans public/assets/vendor/nunito (la CSP est en 'self', un CDN serait bloqué).
      // Base à 14 px : application de gestion à forte densité d'information,
      // pas un site vitrine.
      fontFamily: "'Nunito', system-ui, -apple-system, 'Segoe UI', sans-serif",
      fontSize: 14,
      h1: { fontSize: 24, lineHeight: '32px', fontWeight: 600, letterSpacing: '-0.02em' },
      h2: { fontSize: 20, lineHeight: '28px', fontWeight: 600 },
      h3: { fontSize: 16, lineHeight: '24px', fontWeight: 600 },
      body1: { fontSize: 14, lineHeight: '20px' },
      body2: { fontSize: 13, lineHeight: '18px' },
      caption: { fontSize: 12, lineHeight: '16px' },
      button: { textTransform: 'none', fontWeight: 500 },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Les montants et colonnes numériques doivent s'aligner verticalement :
          // sans chiffres tabulaires, une colonne de prix part en escalier.
          '.tabular': { fontVariantNumeric: 'tabular-nums' },
          ':focus-visible': {
            outline: `2px solid ${dark ? palette.brand[400] : palette.brand[500]}`,
            outlineOffset: 2,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: { root: { borderRadius: radius.md } },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${dark ? palette.neutral[800] : palette.neutral[200]}`,
          },
        },
      },
      MuiTooltip: {
        defaultProps: { arrow: true },
      },
    },
  })
}

export const lightTheme = build('light')
export const darkTheme = build('dark')
export const getTheme = (mode: ThemeMode): Theme => (mode === 'dark' ? darkTheme : lightTheme)
